import { Flex, FormControl, FormLabel, Select, Text, type ThemingProps } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { WholeNumberInput } from "../input/WholeNumberInput";
import { ExtensionDurationUnit } from "../../autogen/bff-api";
import { isDeadlineWithinContract, isWholeExtensionNumber } from "./extensionRules";

export interface ExtensionDraft {
  duration: number | null;
  unit: ExtensionDurationUnit;
  exerciseDeadlineMonths: number | null;
}

interface Props {
  value: ExtensionDraft;
  onChange: (change: Partial<ExtensionDraft>) => void;
  deadlineError?: string | null;
  durationError?: string | null;
  onNumberBlur?: () => void;
  size?: ThemingProps<"Input">["size"];
  extensionNumber?: number;
  contractMonthsBefore?: number | null;
}

export const ExtensionFields = ({
  value,
  onChange,
  deadlineError,
  durationError,
  onNumberBlur,
  size,
  extensionNumber,
  contractMonthsBefore = null,
}: Props) => {
  const { t } = useTranslation();
  const labels =
    extensionNumber == null
      ? { length: t("Length"), unit: t("Unit"), deadline: t("Deadline to exercise") }
      : {
          length: t("Length of extension {{number}}", { number: extensionNumber }),
          unit: t("Unit of extension {{number}}", { number: extensionNumber }),
          deadline: t("Deadline to exercise extension {{number}}", { number: extensionNumber }),
        };
  const durationIsWhole = isWholeExtensionNumber(value.duration);
  const deadlineIsWhole = isWholeExtensionNumber(value.exerciseDeadlineMonths);
  const deadlineIsWithinContract = isDeadlineWithinContract(value.exerciseDeadlineMonths, contractMonthsBefore);

  return (
    <Flex gap="4" wrap="wrap" align="flex-start">
      <FormControl width="auto" isInvalid={!durationIsWhole || !!durationError}>
        <FormLabel fontSize="sm">{t("Length")}</FormLabel>
        <Flex gap="2">
          <WholeNumberInput
            numericValue={value.duration}
            onNumericChange={(n) => onChange({ duration: n })}
            onBlur={onNumberBlur}
            width="24"
            size={size}
            aria-label={String(labels.length)}
          />
          <Select
            value={value.unit}
            width="32"
            size={size}
            aria-label={String(labels.unit)}
            onChange={(e) => onChange({ unit: e.target.value as ExtensionDurationUnit })}
          >
            <option value={ExtensionDurationUnit.Years}>{t("years")}</option>
            <option value={ExtensionDurationUnit.Months}>{t("months")}</option>
          </Select>
        </Flex>
        {!durationIsWhole && <WholeNumberRequired />}
        {durationIsWhole && durationError && (
          <Text fontSize="xs" color="smError" mt="1" data-testid="extension-duration-error">
            {durationError}
          </Text>
        )}
      </FormControl>
      <FormControl width="auto" isInvalid={!deadlineIsWhole || !deadlineIsWithinContract || !!deadlineError}>
        <FormLabel fontSize="sm">{t("Deadline to exercise")}</FormLabel>
        <Flex gap="2" align="center">
          <WholeNumberInput
            numericValue={value.exerciseDeadlineMonths}
            onNumericChange={(n) => onChange({ exerciseDeadlineMonths: n })}
            onBlur={onNumberBlur}
            width="24"
            size={size}
            aria-label={String(labels.deadline)}
          />
          <Text fontSize="sm">{t("months before the contract ends")}</Text>
        </Flex>
        {deadlineIsWhole && deadlineError && (
          <Text fontSize="xs" color="smError" mt="1" data-testid="extension-deadline-error">
            {deadlineError}
          </Text>
        )}
        {!deadlineIsWhole && <WholeNumberRequired />}
        {deadlineIsWhole && !deadlineIsWithinContract && contractMonthsBefore != null && (
          <DeadlineBeyondContract contractMonthsBefore={contractMonthsBefore} />
        )}
      </FormControl>
    </Flex>
  );
};

const WholeNumberRequired = () => {
  const { t } = useTranslation();
  return (
    <Text fontSize="xs" color="smError" mt="1">
      {t("Enter a whole number from 1 to 999")}
    </Text>
  );
};

const DeadlineBeyondContract = ({ contractMonthsBefore }: { contractMonthsBefore: number }) => {
  const { t } = useTranslation();
  return (
    <Text fontSize="xs" color="smError" mt="1">
      {t("Must be shorter than the {{months}} {{unit}} the contract runs before this extension", {
        months: contractMonthsBefore,
        unit: t("month", { count: contractMonthsBefore }),
      })}
    </Text>
  );
};
